import React, { useEffect, useState } from "react";
import { getUser } from "../../../helpers/utils";
import { api } from "../../../helpers/api";
import { identifyUser } from "../../../helpers/analytics";
import { logout } from "../../../helpers/auth-session";

export const UserContext = React.createContext({});

export function UserProvider({ children }) {
  const user = getUser();
  const [onboardingData, setOnboardingData] = useState<Record<string, unknown> | undefined>(undefined);

  const fetchOnboardingData = async () => {
    if (user?.id) {
      const onboardingResponse = await api.getOnboarding(user?.id);
      setOnboardingData(onboardingResponse);
    }
  };

  useEffect(() => {
    fetchOnboardingData();
  }, [user?.id]);

  // Identify the user in PostHog once user + onboarding data are available
  useEffect(() => {
    const metadata = onboardingData?.userMetadata as Record<string, unknown> | undefined;
    const hotelNameStr = metadata?.hotelName as string | undefined;
    if (!user?.id || !hotelNameStr) return;

    identifyUser(user.id, {
      hotelName: hotelNameStr,
      email: user.email || "",
      role: user.type || "",
    });
  }, [user?.id, user?.email, user?.type, onboardingData]);

  // Call updateActivity once when the component mounts
  useEffect(() => {
    const updateActivity = async () => {
      try {
        await api.updateUserActivity(user?.id);
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } };
        if ([400, 401].includes(err?.response?.status || 0)) {
          logout();
        } else {
          console.error("Failed to update user activity", error);
        }
      }
    };

    if (user?.id) {
      updateActivity();
    }
  }, [user?.id]);

  const values = {
    logout,
    onboardingData,
    fetchOnboardingData,
  };

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error("useTags must be used within a UserProvider");
  }
  return context;
}
